import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Instagram, Mail, MapPin, Sparkles } from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import SEO from "@/components/SEO";

export default function About() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [siteImages, setSiteImages] = useState({});

  useEffect(() => {
    api.get("/site-images").then(({ data }) => setSiteImages(data || {})).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill all fields");
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send. Please try emailing us at " + BRAND.email + ".");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="mx-auto max-w-[1440px] px-5 sm:px-10 py-14 sm:py-20" data-testid="about-page">
      <SEO
        title="About & Contact — Star Jewels"
        description="Learn about Star Jewels — a boutique artificial jewelry brand hand-crafting elegant fashion jewellery in India. Reach us on Instagram or by email."
        path="/about"
      />
      {/* Story */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div>
          <Sparkles size={22} className="text-sj-goldDark" />
          <div className="overline mt-3">Our story</div>
          <h1 className="font-display text-4xl sm:text-5xl font-light mt-3 leading-tight">
            A little starlight for the everyday.
          </h1>
          <p className="mt-6 text-sj-inkSoft leading-relaxed">
            Star Jewels was born from a simple belief — that beautiful jewellery should be a part of every day, not saved for occasions. Each piece is designed to be layered, restacked, mixed and matched, so your look grows with you.
          </p>
          <p className="mt-4 text-sj-inkSoft leading-relaxed">
            Every piece is finished by hand. We use gold-tone, rose-gold and silver-tone alloys with skin-friendly coatings — pretty, gentle, and made to be worn a lot.
          </p>
        </div>
        <div className="relative">
          <div className="absolute inset-0 -m-4 border border-sj-gold/40 rounded-sm" />
          <img
            src={siteImages.about ? resolveImage(siteImages.about, { w: 900 }) : "https://images.pexels.com/photos/1454171/pexels-photo-1454171.jpeg?auto=compress&cs=tinysrgb&h=800&w=800"}
            alt="" className="relative w-full aspect-square object-cover rounded-sm" loading="lazy" decoding="async" width="900" height="900" />
        </div>
      </section>

      <div className="gold-divider mx-auto max-w-3xl my-20" />

      {/* Contact */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="overline">Say hello</div>
          <h2 className="font-display text-3xl sm:text-4xl font-light mt-2 leading-tight">
            We’d love to hear from you.
          </h2>
          <p className="mt-4 text-sj-inkSoft">For style advice, custom requests, or a quick chat — reach out anytime.</p>

          <ul className="mt-8 space-y-5">
            <li className="flex items-start gap-4">
              <span className="p-2.5 rounded-full bg-sj-cream text-sj-goldDark"><Instagram size={18} /></span>
              <div>
                <div className="text-xs text-sj-muted">Instagram</div>
                <a href={`https://instagram.com/${BRAND.instagram}`} target="_blank" rel="noreferrer" className="text-sj-ink hover:text-sj-goldDark font-medium">@{BRAND.instagram}</a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="p-2.5 rounded-full bg-sj-cream text-sj-goldDark"><Mail size={18} /></span>
              <div>
                <div className="text-xs text-sj-muted">Email</div>
                <a href={`mailto:${BRAND.email}`} className="text-sj-ink hover:text-sj-goldDark font-medium">{BRAND.email}</a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="p-2.5 rounded-full bg-sj-cream text-sj-goldDark"><MapPin size={18} /></span>
              <div>
                <div className="text-xs text-sj-muted">Ships from</div>
                <div className="text-sj-ink font-medium">India · Nationwide delivery</div>
              </div>
            </li>
          </ul>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 bg-sj-cream rounded-md p-6 sm:p-10" data-testid="contact-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-sj-inkSoft" htmlFor="c-name">Name</label>
              <input id="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 w-full bg-white border border-sj-ink/10 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm transition-colors"
                data-testid="contact-name" />
            </div>
            <div>
              <label className="text-xs text-sj-inkSoft" htmlFor="c-email">Email</label>
              <input id="c-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 w-full bg-white border border-sj-ink/10 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm transition-colors"
                data-testid="contact-email" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-sj-inkSoft" htmlFor="c-msg">Message</label>
              <textarea id="c-msg" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-1.5 w-full bg-white border border-sj-ink/10 focus:border-sj-goldDark outline-none rounded-md px-4 py-3 text-sm"
                data-testid="contact-message" />
            </div>
          </div>
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <button type="submit" disabled={sending} className="btn-primary disabled:opacity-60" data-testid="contact-submit">
              {sending ? "Sending…" : "Send message"}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}
